/*
  munchief-product-main — buy-box interactivity.

  Imported page-wise by sections/munchief-product-main.liquid (defer), once
  per page no matter how many instances render: the initialiser walks every
  .product-main on the page and binds each separately, guarding with a
  data-productMainReady flag so a re-render cannot double-bind. It re-inits
  on shopify:section:load for the theme editor.

  Three behaviours, all from the static build
  (shopify-html/sections/product-main.html):

    1. clicking any photo — the viewer, a grid tile, or the corner
       magnifier — opens the lightbox gallery at that image, which then
       navigates the whole set with arrows, keys or its thumbnail strip
    2. variant pill click re-labels the Add to cart button with that pill's
       price AND writes its variant id into the product form's hidden `id`
       input, so the host theme's cart gets the right variant
    3. quantity stepper counts up/down (floor 1) and mirrors the value into
       the form's hidden `quantity` input

  It also does two things the static markup did inline, because a theme
  block cannot know its own index: it fills an image-less viewer from the
  first thumb, and selects the first variant pill when no block set
  default_selected.
*/
(function () {
  function mainImageOf(section) {
    /* The viewer image comes from the munchief-image snippet, which cannot
       take a data-* hook — so it is addressed by position instead. */
    return section.querySelector('.product-main__media img');
  }

  /* The rail no longer swaps the viewer's photo — every tile opens the
     lightbox at itself instead, which is where a photo is read large now.
     What is left here is the one case the markup cannot cover: a product
     with no images of its own, whose rail is the gallery_image block
     fallback, so the viewer borrows the first tile rather than rendering
     blank beside a filled rail. */
  function initGallery(section) {
    var mainImage = mainImageOf(section);
    var thumbs = section.querySelectorAll('[data-gallery-thumb]');
    if (!mainImage || !thumbs.length) return;

    if (!mainImage.getAttribute('src')) {
      mainImage.src = thumbs[0].getAttribute('data-image') || '';
    }
  }

  /* Lightbox over the page, not a new tab: a raw image URL in a tab has no
     way back to the product except the browser's own back button, and no
     close control at all.

     It is a GALLERY, not a single photo: prev/next arrows, a thumbnail strip
     along the bottom, and arrow-key support, so a visitor who opens one
     image can read the whole set without closing and reopening. Every image
     on the page opens it — the viewer, each grid tile, and the corner
     magnifier — each at its own index.

     The overlay is built here rather than in Liquid so the section ships no
     markup that only matters once JS has run — with JS off, no image opens a
     dead dialog. It is created on first use and then reused. */
  function initLightbox(section) {
    var mainImage = mainImageOf(section);
    var zoomButton = section.querySelector('[data-gallery-zoom]');
    var thumbs = Array.prototype.slice.call(
      section.querySelectorAll('[data-gallery-thumb]')
    );
    if (!mainImage) return;

    /* The gallery in product.images order. The viewer holds the featured
       image and the rail deliberately skips it, so the two concatenate into
       the full set with no repeat — see the rail's offset in
       sections/munchief-product-main.liquid. */
    function collect() {
      var items = [];
      var heroSrc = mainImage.getAttribute('src');
      if (heroSrc) {
        items.push({ full: heroSrc, thumb: heroSrc, alt: mainImage.alt || '' });
      }
      thumbs.forEach(function (thumb) {
        var full = thumb.getAttribute('data-image');
        if (!full) return;
        var preview = thumb.querySelector('img');
        items.push({
          full: full,
          thumb: (preview && preview.getAttribute('src')) || full,
          alt: thumb.getAttribute('aria-label') || ''
        });
      });
      return items;
    }

    var items = [];
    var index = 0;
    var overlay = null;
    var stage = null;
    var strip = null;
    var closeButton = null;
    var lastFocused = null;

    function lockScroll(locked) {
      document.documentElement.style.overflow = locked ? 'hidden' : '';
      document.body.style.overflow = locked ? 'hidden' : '';
    }

    function show(next) {
      if (!items.length) return;
      /* Wraps both ways, so the arrows never dead-end on the first or last
         photo the way disabled buttons would. */
      index = (next + items.length) % items.length;

      stage.src = items[index].full;
      stage.alt = items[index].alt;

      var tiles = strip.children;
      for (var i = 0; i < tiles.length; i++) {
        tiles[i].classList.toggle('is-active', i === index);
        tiles[i].setAttribute('aria-current', i === index ? 'true' : 'false');
      }
    }

    function buildStrip() {
      strip.innerHTML = '';
      items.forEach(function (item, i) {
        var tile = document.createElement('button');
        tile.type = 'button';
        tile.className = 'product-main__lightbox-thumb';
        tile.setAttribute('aria-label', item.alt || 'Show photo ' + (i + 1));
        var img = document.createElement('img');
        img.src = item.thumb;
        img.alt = '';
        tile.appendChild(img);
        tile.addEventListener('click', function () { show(i); });
        strip.appendChild(tile);
      });
    }

    function close() {
      if (!overlay || !overlay.classList.contains('is-open')) return;

      overlay.classList.remove('is-open');
      overlay.setAttribute('aria-hidden', 'true');
      lockScroll(false);
      /* Drop the src so a large photo is not held decoded off-screen. */
      stage.removeAttribute('src');

      if (lastFocused && typeof lastFocused.focus === 'function') {
        lastFocused.focus({ preventScroll: true });
      }
    }

    function arrow(direction, label, path) {
      var button = document.createElement('button');
      button.type = 'button';
      button.className = 'product-main__lightbox-nav product-main__lightbox-nav--' + direction;
      button.setAttribute('aria-label', label);
      button.innerHTML =
        '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"' +
        ' stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' + path + '</svg>';
      return button;
    }

    function build() {
      var label = (zoomButton && zoomButton.getAttribute('data-zoom-close-label')) || 'Close';

      overlay = document.createElement('div');
      overlay.className = 'product-main__lightbox';
      overlay.setAttribute('role', 'dialog');
      overlay.setAttribute('aria-modal', 'true');
      overlay.setAttribute('aria-label', 'Product photos');
      overlay.setAttribute('aria-hidden', 'true');

      stage = document.createElement('img');
      stage.className = 'product-main__lightbox-image';
      stage.alt = '';

      closeButton = document.createElement('button');
      closeButton.type = 'button';
      closeButton.className = 'product-main__lightbox-close';
      closeButton.setAttribute('aria-label', label);
      closeButton.innerHTML =
        '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"' +
        ' stroke-linecap="round" aria-hidden="true">' +
        '<line x1="6" y1="6" x2="18" y2="18"></line>' +
        '<line x1="18" y1="6" x2="6" y2="18"></line></svg>';

      var prev = arrow('prev', 'Previous photo',
        '<line x1="19" y1="12" x2="5" y2="12"></line><polyline points="12 19 5 12 12 5"></polyline>');
      var next = arrow('next', 'Next photo',
        '<line x1="5" y1="12" x2="19" y2="12"></line><polyline points="12 5 19 12 12 19"></polyline>');

      strip = document.createElement('div');
      strip.className = 'product-main__lightbox-strip';

      overlay.appendChild(stage);
      overlay.appendChild(closeButton);
      overlay.appendChild(prev);
      overlay.appendChild(next);
      overlay.appendChild(strip);

      closeButton.addEventListener('click', close);
      prev.addEventListener('click', function () { show(index - 1); });
      next.addEventListener('click', function () { show(index + 1); });

      /* Backdrop only — a click that lands on the photo itself must not
         dismiss the thing the reader just opened. */
      overlay.addEventListener('click', function (event) {
        if (event.target === overlay) close();
      });

      /* Body, not the section: .product-main clips its gallery frame and
         sits inside the page's own stacking contexts, either of which would
         trap a fixed overlay. The .munchief host goes with it — every rule
         in this theme's CSS is namespaced under that class, so an overlay
         parked directly on <body> would render unstyled. */
      var host = document.createElement('div');
      host.className = 'munchief';
      host.appendChild(overlay);
      document.body.appendChild(host);

      /* A real cycle, not the old "swallow Tab": the dialog holds several
         controls now, so Tab has to move between them and wrap at the ends
         rather than be blocked outright. */
      overlay.addEventListener('keydown', function (event) {
        if (event.key !== 'Tab') return;
        var focusable = overlay.querySelectorAll('button');
        if (!focusable.length) return;
        var first = focusable[0];
        var last = focusable[focusable.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      });
    }

    function open(at) {
      /* Rebuilt per open: the rail's tiles can be re-rendered by the theme
         editor between one open and the next. */
      items = collect();
      if (!items.length) return;

      if (!overlay) build();
      buildStrip();

      lastFocused = document.activeElement;
      show(at);
      overlay.classList.add('is-open');
      overlay.setAttribute('aria-hidden', 'false');
      lockScroll(true);
      closeButton.focus({ preventScroll: true });
    }

    if (zoomButton) {
      zoomButton.addEventListener('click', function () { open(0); });
    }

    /* The viewer opens at its own photo; each tile opens at itself. The
       hero is index 0 because the rail starts at the product's SECOND
       image, so a tile's index is its position in the rail plus one. */
    mainImage.addEventListener('click', function () { open(0); });

    thumbs.forEach(function (thumb, i) {
      thumb.addEventListener('click', function () { open(i + 1); });
    });

    document.addEventListener('keydown', function (event) {
      if (!overlay || !overlay.classList.contains('is-open')) return;
      if (event.key === 'Escape') close();
      if (event.key === 'ArrowLeft') show(index - 1);
      if (event.key === 'ArrowRight') show(index + 1);
    });
  }

  function initVariants(section) {
    var pills = section.querySelectorAll('.variant-pill');
    if (!pills.length) return;

    var label = section.querySelector('[data-add-to-cart-label]');
    var variantInput = section.querySelector('[data-variant-input]');

    /* Keep the button's own wording (and its translation) — only the price
       after the em dash is rewritten. */
    var prefix = label ? label.textContent.split('—')[0] : '';

    var selected = section.querySelector('.variant-pill.is-selected');
    if (!selected) {
      selected = pills[0];
      selected.classList.add('is-selected');
    }

    /* The price line above the pills belongs to the selected variant too.
       Only real-variant pills carry data-variant-id; the design's fallback
       pills keep the section's own placeholder pricing. */
    var priceBox = section.querySelector('.product-main__price');

    function renderPrice(pill) {
      if (!priceBox || !pill.getAttribute('data-variant-id')) return;
      var compare = pill.getAttribute('data-variant-compare');
      var save = pill.getAttribute('data-variant-save');
      priceBox.textContent = '';
      function add(cls, text) {
        var el = document.createElement('span');
        el.className = cls;
        el.textContent = text;
        priceBox.appendChild(el);
      }
      add('price__sale', pill.getAttribute('data-variant-price'));
      if (compare) add('price__compare', compare);
      if (compare && save) add('badge badge--save', save);
    }

    function apply(pill) {
      pills.forEach(function (p) { p.classList.remove('is-selected'); });
      pill.classList.add('is-selected');

      if (label) {
        label.textContent = prefix + '— ' + pill.getAttribute('data-variant-price');
      }

      renderPrice(pill);

      var variantId = pill.getAttribute('data-variant-id');
      if (variantInput && variantId) {
        variantInput.value = variantId;
      }
    }

    /* Sync the form to whichever pill opens selected, so a first submit
       cannot post the product's default variant behind the visitor's back. */
    renderPrice(selected);

    if (selected.getAttribute('data-variant-id') && variantInput) {
      variantInput.value = selected.getAttribute('data-variant-id');
    }

    pills.forEach(function (pill) {
      pill.addEventListener('click', function () { apply(pill); });
    });
  }

  function initQty(section) {
    var value = section.querySelector('[data-qty-value]');
    var input = section.querySelector('[data-qty-input]');
    var down = section.querySelector('[data-qty-decrease]');
    var up = section.querySelector('[data-qty-increase]');
    if (!value || !down || !up) return;

    function set(next) {
      value.textContent = next;
      if (input) input.value = next;
    }

    down.addEventListener('click', function () {
      set(Math.max(1, parseInt(value.textContent, 10) - 1));
    });
    up.addEventListener('click', function () {
      set(parseInt(value.textContent, 10) + 1);
    });
  }

  function initSection(section) {
    if (section.dataset.productMainReady === 'true') return;
    section.dataset.productMainReady = 'true';

    initGallery(section);
    initLightbox(section);
    initVariants(section);
    initQty(section);
  }

  function initAll(root) {
    (root || document).querySelectorAll('.product-main').forEach(initSection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(); });
  } else {
    initAll();
  }

  /* Theme editor: re-bind when a section is added or re-rendered. */
  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target);
  });
})();
